// src/components/chatbot/ChatHeader.tsx
import React from 'react';
import { X, Maximize2, Minimize2, Trash2, Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

type ChatHeaderProps = {
  botName: string;
  botAvatar: string;
  isMinimized: boolean;
  onMinimize: () => void;
  onClose: () => void;
  onClearChat: () => void;
};

export const ChatHeader: React.FC<ChatHeaderProps> = ({
  botName,
  botAvatar,
  isMinimized,
  onMinimize,
  onClose,
  onClearChat,
}) => {
  return (
    <div className="bg-primary text-primary-foreground p-3 flex items-center justify-between">
      <div className="flex items-center">
        <Avatar className="h-8 w-8 mr-2 bg-white/20">
          <AvatarImage src={botAvatar} alt={botName} />
          <AvatarFallback className="bg-white/20 text-white">
            <Bot className="h-4 w-4" />
          </AvatarFallback>
        </Avatar>
        <span className="font-medium">{botName}</span>
      </div>
      <div className="flex items-center space-x-1">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-primary-foreground hover:bg-white"
                onClick={onClearChat}
                aria-label="Clear chat history"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Clear chat history</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-primary-foreground hover:bg-white"
                onClick={onMinimize}
                aria-label={isMinimized ? "Maximize chat" : "Minimize chat"}
              >
                {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{isMinimized ? "Maximize chat" : "Minimize chat"}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-primary-foreground hover:bg-white"
                onClick={onClose}
                aria-label="Close chat"
              >
                <X className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Close chat</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

export default ChatHeader;